import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Typography, Space, Tag, Table, Button, Statistic, Divider, Select } from 'antd';
import { 
  LeftOutlined, 
  DollarOutlined, 
  ClockCircleOutlined, 
  ShopOutlined, 
  InfoCircleOutlined, 
  FilterOutlined, 
  AppstoreOutlined,
  RiseOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

// 定义接口
interface RoomTypeData {
  id: string;
  name: string;
  currentPrice: number;
  suggestedPrice: number;
  competitorAvgPrice: number;
  changeRatio: number;
  roomsAvailable: number;
  totalRooms: number;
}

interface CompetitorData {
  id: string;
  name: string;
  currentPrice: number;
  priceChange: number;
  changeType: 'increase' | 'decrease' | 'unchanged';
  distance: string;
  star: number;
  roomsAvailable?: number;
  totalRooms?: number;
  roomTypeId?: string;
}

interface PriceTrendData {
  date: string;
  currentPrice: number;
  suggestedPrice: number;
  competitorAvgPrice: number;
  roomTypePrices?: {
    [key: string]: {
      currentPrice: number;
      suggestedPrice: number;
      competitorAvgPrice: number;
    }
  }
}

interface RevenueImpactData {
  scenario: string;
  additionalRevenue: number;
  occupancyRate: number;
  averagePrice: number;
}

interface EventData {
  id: string;
  name: string;
  date: string;
  type: '节假日' | '展会' | '赛事' | '促销' | '其他';
  impact: 'high' | 'medium' | 'low';
  description: string;
}

interface AlertData {
  id: string;
  title: string;
  content: string;
  time: string;
  store: string;
  type: 'timePoint' | 'competitor' | 'roomStatus';
  targetDate: string;
  currentPrice: number;
  suggestedPrice: number;
  changeRatio: number;
  changeAmount: number;
  reasonSummary: string;
  severity: 'high' | 'medium' | 'low';
  roomTypes: RoomTypeData[];
  competitors: CompetitorData[];
  priceTrend: PriceTrendData[];
  revenueImpact: RevenueImpactData[];
  events: EventData[];
}

const PriceChangeAlert: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  // 添加房型选择状态
  const [selectedRoomTypeId, setSelectedRoomTypeId] = useState<string>('all');
  // 添加竞品房型筛选状态
  const [competitorRoomTypeId, setCompetitorRoomTypeId] = useState<string>('all');

  // 模拟数据 - 实际项目中应从API获取
  const [alertData, setAlertData] = useState<AlertData>({
    id: id || '1',
    title: '时间点调价提醒',
    content: '系统检测到7月27-28日为周末高峰，建议提前调高房价以提升收益',
    time: '2023-07-20 10:15',
    store: '秋果花园酒店（西湖店）',
    type: 'timePoint',
    targetDate: '2023-07-27',
    currentPrice: 358,
    suggestedPrice: 412,
    changeRatio: 15,
    changeAmount: 54,
    reasonSummary: '周末高峰期间，市场需求增加，价格弹性较低，提高房价可获得更多收益',
    severity: 'medium',
    roomTypes: [
      {
        id: '1',
        name: '标准大床房',
        currentPrice: 358,
        suggestedPrice: 412,
        competitorAvgPrice: 389,
        changeRatio: 15,
        roomsAvailable: 25,
        totalRooms: 30
      },
      {
        id: '2',
        name: '豪华大床房',
        currentPrice: 458,
        suggestedPrice: 527,
        competitorAvgPrice: 499,
        changeRatio: 15,
        roomsAvailable: 18,
        totalRooms: 20
      },
      {
        id: '3',
        name: '商务套房',
        currentPrice: 658,
        suggestedPrice: 757,
        competitorAvgPrice: 699,
        changeRatio: 15,
        roomsAvailable: 8,
        totalRooms: 10
      }
    ],
    competitors: [
      {
        id: '1',
        name: '西湖雅园酒店',
        currentPrice: 399,
        priceChange: 30,
        changeType: 'increase',
        distance: '0.8km',
        star: 4.5,
        roomsAvailable: 15,
        totalRooms: 25,
        roomTypeId: '1'
      },
      {
        id: '2',
        name: '杭州湖畔酒店',
        currentPrice: 379,
        priceChange: 0,
        changeType: 'unchanged',
        distance: '1.2km',
        star: 4.0,
        roomsAvailable: 12,
        totalRooms: 20,
        roomTypeId: '1'
      },
      {
        id: '3',
        name: '西子湖四季酒店',
        currentPrice: 499,
        priceChange: 50,
        changeType: 'increase',
        distance: '1.5km',
        star: 5.0,
        roomsAvailable: 8,
        totalRooms: 30,
        roomTypeId: '1'
      },
      {
        id: '4',
        name: '西湖雅园酒店',
        currentPrice: 499,
        priceChange: 30,
        changeType: 'increase',
        distance: '0.8km',
        star: 4.5,
        roomsAvailable: 10,
        totalRooms: 20,
        roomTypeId: '2'
      },
      {
        id: '5',
        name: '杭州湖畔酒店',
        currentPrice: 479,
        priceChange: 0,
        changeType: 'unchanged',
        distance: '1.2km',
        star: 4.0,
        roomsAvailable: 8,
        totalRooms: 15,
        roomTypeId: '2'
      },
      {
        id: '6',
        name: '西子湖四季酒店',
        currentPrice: 599,
        priceChange: 50,
        changeType: 'increase',
        distance: '1.5km',
        star: 5.0,
        roomsAvailable: 5,
        totalRooms: 25,
        roomTypeId: '3'
      }
    ],
    priceTrend: [
      { 
        date: '07-25', 
        currentPrice: 338, 
        suggestedPrice: 338, 
        competitorAvgPrice: 349,
        roomTypePrices: {
          '1': { currentPrice: 338, suggestedPrice: 338, competitorAvgPrice: 349 },
          '2': { currentPrice: 438, suggestedPrice: 438, competitorAvgPrice: 459 },
          '3': { currentPrice: 638, suggestedPrice: 638, competitorAvgPrice: 649 }
        }
      },
      { 
        date: '07-26', 
        currentPrice: 348, 
        suggestedPrice: 348, 
        competitorAvgPrice: 359,
        roomTypePrices: {
          '1': { currentPrice: 348, suggestedPrice: 348, competitorAvgPrice: 359 },
          '2': { currentPrice: 448, suggestedPrice: 448, competitorAvgPrice: 469 },
          '3': { currentPrice: 648, suggestedPrice: 648, competitorAvgPrice: 659 }
        }
      },
      { 
        date: '07-27', 
        currentPrice: 358, 
        suggestedPrice: 412, 
        competitorAvgPrice: 425,
        roomTypePrices: {
          '1': { currentPrice: 358, suggestedPrice: 412, competitorAvgPrice: 425 },
          '2': { currentPrice: 458, suggestedPrice: 527, competitorAvgPrice: 499 },
          '3': { currentPrice: 658, suggestedPrice: 757, competitorAvgPrice: 699 }
        }
      },
      { 
        date: '07-28', 
        currentPrice: 358, 
        suggestedPrice: 412, 
        competitorAvgPrice: 430,
        roomTypePrices: {
          '1': { currentPrice: 358, suggestedPrice: 412, competitorAvgPrice: 430 },
          '2': { currentPrice: 458, suggestedPrice: 527, competitorAvgPrice: 510 },
          '3': { currentPrice: 658, suggestedPrice: 757, competitorAvgPrice: 710 }
        }
      },
      { 
        date: '07-29', 
        currentPrice: 348, 
        suggestedPrice: 383, 
        competitorAvgPrice: 389,
        roomTypePrices: {
          '1': { currentPrice: 348, suggestedPrice: 383, competitorAvgPrice: 389 },
          '2': { currentPrice: 448, suggestedPrice: 493, competitorAvgPrice: 479 },
          '3': { currentPrice: 648, suggestedPrice: 713, competitorAvgPrice: 679 }
        }
      },
      { 
        date: '07-30', 
        currentPrice: 338, 
        suggestedPrice: 338, 
        competitorAvgPrice: 349,
        roomTypePrices: {
          '1': { currentPrice: 338, suggestedPrice: 338, competitorAvgPrice: 349 },
          '2': { currentPrice: 438, suggestedPrice: 438, competitorAvgPrice: 459 },
          '3': { currentPrice: 638, suggestedPrice: 638, competitorAvgPrice: 649 }
        }
      }
    ],
    revenueImpact: [
      {
        scenario: '维持现价',
        additionalRevenue: 0,
        occupancyRate: 95,
        averagePrice: 358
      },
      {
        scenario: '采纳建议价',
        additionalRevenue: 2850,
        occupancyRate: 90,
        averagePrice: 412
      },
      {
        scenario: '小幅调价',
        additionalRevenue: 1425,
        occupancyRate: 92,
        averagePrice: 385
      }
    ],
    events: [
      {
        id: '1',
        name: '西湖音乐节',
        date: '2023-07-27',
        type: '赛事',
        impact: 'high',
        description: '为期2天的音乐节活动，预计吸引游客3万人'
      },
      {
        id: '2',
        name: '周末出行高峰',
        date: '2023-07-28',
        type: '其他',
        impact: 'medium',
        description: '周末期间商旅及家庭出游需求增加'
      }
    ]
  });

  // 竞品联动调价数据
  const competitorAlertData: AlertData = {
    id: '7',
    title: '竞品联动调价提醒',
    content: '竞品"西湖雅园酒店"已上调7月30日房价至¥888，建议适当跟进调价',
    time: '2023-07-21 16:30',
    store: '秋果花园酒店（西湖店）',
    type: 'competitor',
    targetDate: '2023-07-30',
    currentPrice: 368,
    suggestedPrice: 423,
    changeRatio: 15,
    changeAmount: 55,
    reasonSummary: '主要竞争对手已提高价格，市场需求旺盛，跟进调价可维持价格竞争力并提高收益',
    severity: 'high',
    roomTypes: [
      {
        id: '1',
        name: '标准大床房',
        currentPrice: 368,
        suggestedPrice: 423,
        competitorAvgPrice: 468,
        changeRatio: 15,
        roomsAvailable: 20,
        totalRooms: 30
      },
      {
        id: '2',
        name: '豪华大床房',
        currentPrice: 468,
        suggestedPrice: 538,
        competitorAvgPrice: 588,
        changeRatio: 15,
        roomsAvailable: 15,
        totalRooms: 20
      },
      {
        id: '3',
        name: '商务套房',
        currentPrice: 688,
        suggestedPrice: 791,
        competitorAvgPrice: 888,
        changeRatio: 15,
        roomsAvailable: 8,
        totalRooms: 10
      }
    ],
    competitors: [
      {
        id: '1',
        name: '西湖雅园酒店',
        currentPrice: 468,
        priceChange: 150,
        changeType: 'increase',
        distance: '0.8km',
        star: 4.5,
        roomsAvailable: 10,
        totalRooms: 25,
        roomTypeId: '1'
      },
      {
        id: '2',
        name: '杭州湖畔酒店',
        currentPrice: 428,
        priceChange: 80,
        changeType: 'increase',
        distance: '1.2km',
        star: 4.0,
        roomsAvailable: 12,
        totalRooms: 20,
        roomTypeId: '1'
      },
      {
        id: '3',
        name: '西子湖四季酒店',
        currentPrice: 508,
        priceChange: 20,
        changeType: 'increase',
        distance: '1.5km',
        star: 5.0,
        roomsAvailable: 18,
        totalRooms: 30,
        roomTypeId: '1'
      },
      {
        id: '4',
        name: '西湖国际酒店',
        currentPrice: 468,
        priceChange: 90,
        changeType: 'increase',
        distance: '2.0km',
        star: 4.8,
        roomsAvailable: 15,
        totalRooms: 25,
        roomTypeId: '1'
      },
      {
        id: '5',
        name: '西湖雅园酒店',
        currentPrice: 568,
        priceChange: 150,
        changeType: 'increase',
        distance: '0.8km',
        star: 4.5,
        roomsAvailable: 8,
        totalRooms: 20,
        roomTypeId: '2'
      },
      {
        id: '6',
        name: '西子湖四季酒店',
        currentPrice: 788,
        priceChange: 20,
        changeType: 'increase',
        distance: '1.5km',
        star: 5.0,
        roomsAvailable: 5,
        totalRooms: 15,
        roomTypeId: '3'
      }
    ],
    priceTrend: [
      { 
        date: '07-25', 
        currentPrice: 358, 
        suggestedPrice: 358, 
        competitorAvgPrice: 370,
        roomTypePrices: {
          '1': { currentPrice: 358, suggestedPrice: 358, competitorAvgPrice: 370 },
          '2': { currentPrice: 458, suggestedPrice: 458, competitorAvgPrice: 470 },
          '3': { currentPrice: 658, suggestedPrice: 658, competitorAvgPrice: 670 }
        }
      },
      { 
        date: '07-26', 
        currentPrice: 358, 
        suggestedPrice: 358, 
        competitorAvgPrice: 385,
        roomTypePrices: {
          '1': { currentPrice: 358, suggestedPrice: 358, competitorAvgPrice: 385 },
          '2': { currentPrice: 458, suggestedPrice: 458, competitorAvgPrice: 485 },
          '3': { currentPrice: 658, suggestedPrice: 658, competitorAvgPrice: 685 }
        }
      },
      { 
        date: '07-27', 
        currentPrice: 358, 
        suggestedPrice: 358, 
        competitorAvgPrice: 410,
        roomTypePrices: {
          '1': { currentPrice: 358, suggestedPrice: 358, competitorAvgPrice: 410 },
          '2': { currentPrice: 458, suggestedPrice: 458, competitorAvgPrice: 510 },
          '3': { currentPrice: 658, suggestedPrice: 658, competitorAvgPrice: 710 }
        }
      },
      { 
        date: '07-28', 
        currentPrice: 358, 
        suggestedPrice: 398, 
        competitorAvgPrice: 420,
        roomTypePrices: {
          '1': { currentPrice: 358, suggestedPrice: 398, competitorAvgPrice: 420 },
          '2': { currentPrice: 458, suggestedPrice: 498, competitorAvgPrice: 520 },
          '3': { currentPrice: 658, suggestedPrice: 698, competitorAvgPrice: 720 }
        }
      },
      { 
        date: '07-29', 
        currentPrice: 368, 
        suggestedPrice: 418, 
        competitorAvgPrice: 440,
        roomTypePrices: {
          '1': { currentPrice: 368, suggestedPrice: 418, competitorAvgPrice: 440 },
          '2': { currentPrice: 468, suggestedPrice: 518, competitorAvgPrice: 540 },
          '3': { currentPrice: 668, suggestedPrice: 738, competitorAvgPrice: 740 }
        }
      },
      { 
        date: '07-30', 
        currentPrice: 368, 
        suggestedPrice: 448, 
        competitorAvgPrice: 468,
        roomTypePrices: {
          '1': { currentPrice: 368, suggestedPrice: 448, competitorAvgPrice: 468 },
          '2': { currentPrice: 468, suggestedPrice: 558, competitorAvgPrice: 568 },
          '3': { currentPrice: 688, suggestedPrice: 788, competitorAvgPrice: 868 }
        }
      }
    ],
    revenueImpact: [
      {
        scenario: '维持现价',
        additionalRevenue: 0,
        occupancyRate: 92,
        averagePrice: 368
      },
      {
        scenario: '采纳建议价',
        additionalRevenue: 4200,
        occupancyRate: 88,
        averagePrice: 448
      },
      {
        scenario: '小幅调价',
        additionalRevenue: 2300,
        occupancyRate: 90,
        averagePrice: 408
      }
    ],
    events: [
      {
        id: '1',
        name: '暑期旅游旺季',
        date: '2023-07-30',
        type: '节假日',
        impact: 'high',
        description: '暑期旅游旺季期间游客大量增加，酒店周边景点游客爆满'
      }
    ]
  };

  // 房态变化调价数据
  const roomStatusAlertData: AlertData = {
    id: '8',
    title: '房态变化调价提醒',
    content: '8月2日豪华大床房库存剩余不足20%，建议上调价格优化收益',
    time: '2023-07-22 09:45',
    store: '秋果花园酒店（西湖店）',
    type: 'roomStatus',
    targetDate: '2023-08-02',
    currentPrice: 388,
    suggestedPrice: 446,
    changeRatio: 15,
    changeAmount: 58,
    reasonSummary: '当前房态库存紧张，市场需求仍保持强劲，提价可优化收益并控制售卖速度',
    severity: 'high',
    roomTypes: [
      {
        id: '1',
        name: '标准大床房',
        currentPrice: 388,
        suggestedPrice: 446,
        competitorAvgPrice: 399,
        changeRatio: 15,
        roomsAvailable: 15,
        totalRooms: 30
      },
      {
        id: '2',
        name: '豪华大床房',
        currentPrice: 488,
        suggestedPrice: 561,
        competitorAvgPrice: 499,
        changeRatio: 15,
        roomsAvailable: 3,
        totalRooms: 20
      },
      {
        id: '3',
        name: '商务套房',
        currentPrice: 688,
        suggestedPrice: 791,
        competitorAvgPrice: 699,
        changeRatio: 15,
        roomsAvailable: 2,
        totalRooms: 10
      }
    ],
    competitors: [
      {
        id: '1',
        name: '西湖雅园酒店',
        currentPrice: 389,
        priceChange: 0,
        changeType: 'unchanged',
        distance: '0.8km',
        star: 4.5,
        roomsAvailable: 18,
        totalRooms: 30,
        roomTypeId: '1'
      },
      {
        id: '2',
        name: '杭州湖畔酒店',
        currentPrice: 409,
        priceChange: 20,
        changeType: 'increase',
        distance: '1.2km',
        star: 4.0,
        roomsAvailable: 15,
        totalRooms: 25,
        roomTypeId: '1'
      },
      {
        id: '3',
        name: '西子湖四季酒店',
        currentPrice: 399,
        priceChange: -20,
        changeType: 'decrease',
        distance: '1.5km',
        star: 5.0,
        roomsAvailable: 22,
        totalRooms: 35,
        roomTypeId: '1'
      },
      {
        id: '4',
        name: '西湖雅园酒店',
        currentPrice: 489,
        priceChange: 0,
        changeType: 'unchanged',
        distance: '0.8km',
        star: 4.5,
        roomsAvailable: 12,
        totalRooms: 20,
        roomTypeId: '2'
      },
      {
        id: '5',
        name: '杭州湖畔酒店',
        currentPrice: 509,
        priceChange: 20,
        changeType: 'increase',
        distance: '1.2km',
        star: 4.0,
        roomsAvailable: 10,
        totalRooms: 18,
        roomTypeId: '2'
      },
      {
        id: '6',
        name: '西子湖四季酒店',
        currentPrice: 699,
        priceChange: -20,
        changeType: 'decrease',
        distance: '1.5km',
        star: 5.0,
        roomsAvailable: 6,
        totalRooms: 10,
        roomTypeId: '3'
      }
    ],
    priceTrend: [
      { 
        date: '07-29', 
        currentPrice: 388, 
        suggestedPrice: 388, 
        competitorAvgPrice: 399,
        roomTypePrices: {
          '1': { currentPrice: 388, suggestedPrice: 388, competitorAvgPrice: 399 },
          '2': { currentPrice: 488, suggestedPrice: 488, competitorAvgPrice: 499 },
          '3': { currentPrice: 688, suggestedPrice: 688, competitorAvgPrice: 699 }
        }
      },
      { 
        date: '07-30', 
        currentPrice: 388, 
        suggestedPrice: 388, 
        competitorAvgPrice: 399,
        roomTypePrices: {
          '1': { currentPrice: 388, suggestedPrice: 388, competitorAvgPrice: 399 },
          '2': { currentPrice: 488, suggestedPrice: 488, competitorAvgPrice: 499 },
          '3': { currentPrice: 688, suggestedPrice: 688, competitorAvgPrice: 699 }
        }
      },
      { 
        date: '07-31', 
        currentPrice: 388, 
        suggestedPrice: 408, 
        competitorAvgPrice: 399,
        roomTypePrices: {
          '1': { currentPrice: 388, suggestedPrice: 408, competitorAvgPrice: 399 },
          '2': { currentPrice: 488, suggestedPrice: 538, competitorAvgPrice: 499 },
          '3': { currentPrice: 688, suggestedPrice: 718, competitorAvgPrice: 699 }
        }
      },
      { 
        date: '08-01', 
        currentPrice: 388, 
        suggestedPrice: 428, 
        competitorAvgPrice: 399,
        roomTypePrices: {
          '1': { currentPrice: 388, suggestedPrice: 428, competitorAvgPrice: 399 },
          '2': { currentPrice: 488, suggestedPrice: 548, competitorAvgPrice: 499 },
          '3': { currentPrice: 688, suggestedPrice: 728, competitorAvgPrice: 699 }
        }
      },
      { 
        date: '08-02', 
        currentPrice: 388, 
        suggestedPrice: 446, 
        competitorAvgPrice: 399,
        roomTypePrices: {
          '1': { currentPrice: 388, suggestedPrice: 446, competitorAvgPrice: 399 },
          '2': { currentPrice: 488, suggestedPrice: 561, competitorAvgPrice: 499 },
          '3': { currentPrice: 688, suggestedPrice: 791, competitorAvgPrice: 699 }
        }
      },
      { 
        date: '08-03', 
        currentPrice: 388, 
        suggestedPrice: 428, 
        competitorAvgPrice: 399,
        roomTypePrices: {
          '1': { currentPrice: 388, suggestedPrice: 408, competitorAvgPrice: 399 },
          '2': { currentPrice: 488, suggestedPrice: 538, competitorAvgPrice: 499 },
          '3': { currentPrice: 688, suggestedPrice: 718, competitorAvgPrice: 699 }
        }
      }
    ],
    revenueImpact: [
      {
        scenario: '维持现价',
        additionalRevenue: 0,
        occupancyRate: 100,
        averagePrice: 388
      },
      {
        scenario: '采纳建议价',
        additionalRevenue: 3080,
        occupancyRate: 85,
        averagePrice: 465
      },
      {
        scenario: '小幅调价',
        additionalRevenue: 1600,
        occupancyRate: 93,
        averagePrice: 428
      }
    ],
    events: [
      {
        id: '1',
        name: '杭州电子商务展',
        date: '2023-08-02',
        type: '展会',
        impact: 'medium',
        description: '为期3天的行业展会，预计商务客流量增加30%'
      },
      {
        id: '2',
        name: '城西商圈促销活动',
        date: '2023-08-03',
        type: '促销',
        impact: 'low',
        description: '临近商圈大型促销活动，预计增加周边人流'
      }
    ]
  };

  // 动态设置或更新数据，根据调价类型
  React.useEffect(() => {
    // 在实际应用中，这里可以根据ID从API获取数据
    const updateData = () => {
      // 根据URL参数动态更新数据
      if (id === '7') {
        setAlertData(competitorAlertData);
      } else if (id === '8') {
        setAlertData(roomStatusAlertData);
      }
      // 默认是时间点调价(id=6或其他)，保持原有数据
    };

    updateData();
  }, [id]);

  // 获取严重程度对应的颜色
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return '#ff4d4f';
      case 'medium':
        return '#faad14';
      case 'low':
        return '#52c41a';
      default:
        return '#faad14';
    }
  };

  // 获取调价类型图标和样式
  const getPriceChangeTypeIcon = (type: string) => {
    switch (type) {
      case 'timePoint':
        return { 
          icon: <ClockCircleOutlined />, 
          color: '#1890ff',
          text: '时间点调价' 
        };
      case 'competitor':
        return { 
          icon: <ShopOutlined />, 
          color: '#722ed1',
          text: '竞品联动调价' 
        };
      case 'roomStatus':
        return { 
          icon: <AppstoreOutlined />, 
          color: '#eb2f96',
          text: '房态变化调价' 
        };
      default:
        return { 
          icon: <DollarOutlined />, 
          color: '#52c41a',
          text: '调价提醒' 
        };
    }
  };

  // 获取事件类型图标
  const getEventTypeIcon = (type: string) => {
    switch (type) {
      case '节假日':
        return { icon: <ClockCircleOutlined />, color: '#722ed1' };
      case '展会':
        return { icon: <ShopOutlined />, color: '#13c2c2' };
      case '赛事':
        return { icon: <RiseOutlined />, color: '#fa8c16' };
      case '促销':
        return { icon: <DollarOutlined />, color: '#eb2f96' };
      case '其他':
        return { icon: <InfoCircleOutlined />, color: '#8c8c8c' };
      default:
        return { icon: <InfoCircleOutlined />, color: '#8c8c8c' };
    }
  };

  // 获取影响程度颜色
  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high':
        return '#f5222d';
      case 'medium':
        return '#faad14';
      case 'low':
        return '#52c41a';
      default:
        return '#8c8c8c';
    }
  };

  // 获取影响程度文本
  const getImpactText = (impact: string) => {
    switch (impact) {
      case 'high':
        return '高';
      case 'medium':
        return '中';
      case 'low':
        return '低';
      default:
        return '未知';
    }
  };

  // 价格趋势图配置
  const getPriceTrendOptions = () => {
    const dates = alertData.priceTrend.map(item => item.date);
    let currentPrices, suggestedPrices, competitorPrices;
    
    // 根据选择的房型选择相应的价格数据
    if (selectedRoomTypeId === 'all') {
      // 显示全部房型的均价（默认值）
      currentPrices = alertData.priceTrend.map(item => item.currentPrice);
      suggestedPrices = alertData.priceTrend.map(item => item.suggestedPrice);
      competitorPrices = alertData.priceTrend.map(item => item.competitorAvgPrice);
    } else {
      // 显示特定房型的价格
      currentPrices = alertData.priceTrend.map(item => 
        item.roomTypePrices?.[selectedRoomTypeId]?.currentPrice || item.currentPrice);
      suggestedPrices = alertData.priceTrend.map(item => 
        item.roomTypePrices?.[selectedRoomTypeId]?.suggestedPrice || item.suggestedPrice);
      competitorPrices = alertData.priceTrend.map(item => 
        item.roomTypePrices?.[selectedRoomTypeId]?.competitorAvgPrice || item.competitorAvgPrice);
    }

    return {
      tooltip: {
        trigger: 'axis',
        formatter: function(params: any) {
          let result = `${params[0].name}<br/>`;
          params.forEach((param: any) => {
            result += 
              `<div style="display:flex;justify-content:space-between;min-width:150px">
                <span>${param.seriesName}:</span>
                <span style="font-weight:bold">¥${param.value}</span>
              </div>`;
          });
          return result;
        }
      },
      legend: {
        data: ['当前价格', '建议价格', '竞品均价'],
        bottom: 0,
        textStyle: {
          fontSize: 12
        }
      },
      grid: {
        top: '10%',
        left: '3%',
        right: '4%',
        bottom: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: dates,
        axisLabel: {
          fontSize: 12,
          formatter: function(value: string, index: number) {
            // 高亮目标日期
            const isTargetDate = value === dayjs(alertData.targetDate).format('MM-DD');
            if (isTargetDate) {
              return `{a|${value}}`;
            }
            return value;
          },
          rich: {
            a: {
              color: '#ff4d4f',
              fontWeight: 'bold',
              backgroundColor: '#fff1f0',
              padding: [2, 4],
              borderRadius: 2
            }
          }
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          fontSize: 12,
          formatter: '¥{value}'
        },
        name: '房价',
        nameTextStyle: {
          fontSize: 12
        }
      },
      series: [
        {
          name: '当前价格',
          type: 'line',
          data: currentPrices,
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: {
            color: '#1890ff'
          },
          lineStyle: {
            width: 2
          }
        },
        {
          name: '建议价格',
          type: 'line',
          data: suggestedPrices,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: '#ff4d4f'
          },
          lineStyle: {
            width: 3
          },
          label: {
            show: true,
            position: 'top',
            formatter: '¥{c}',
            fontSize: 12
          }
        },
        {
          name: '竞品均价',
          type: 'line',
          data: competitorPrices,
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: {
            color: '#722ed1'
          },
          lineStyle: {
            width: 2,
            type: 'dashed'
          }
        }
      ]
    };
  };

  // 收益影响预测图配置
  const getRevenueImpactOptions = () => {
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: function(params: any) {
          const dataIndex = params[0].dataIndex;
          const scenario = alertData.revenueImpact[dataIndex].scenario;
          const additionalRevenue = alertData.revenueImpact[dataIndex].additionalRevenue;
          const occupancyRate = alertData.revenueImpact[dataIndex].occupancyRate;
          const averagePrice = alertData.revenueImpact[dataIndex].averagePrice;
          
          return `<div style="font-weight:bold">${scenario}</div>
                  <div>额外收益: ¥${additionalRevenue}</div>
                  <div>预估入住率: ${occupancyRate}%</div>
                  <div>平均房价: ¥${averagePrice}</div>`;
        }
      },
      grid: {
        top: '10%',
        left: '3%',
        right: '4%',
        bottom: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: alertData.revenueImpact.map(item => item.scenario),
        axisLabel: {
          fontSize: 12
        }
      },
      yAxis: {
        type: 'value',
        name: '额外收益(¥)',
        nameTextStyle: {
          fontSize: 12
        },
        axisLabel: {
          fontSize: 12
        }
      },
      series: [
        {
          name: '预计额外收益',
          type: 'bar',
          data: alertData.revenueImpact.map(item => item.additionalRevenue),
          itemStyle: {
            color: function(params: any) {
              // 根据情景给不同的颜色
              const colors = ['#d9d9d9', '#ff4d4f', '#faad14'];
              return colors[params.dataIndex];
            }
          },
          label: {
            show: true,
            position: 'top',
            formatter: '¥{c}',
            fontSize: 12
          },
          barWidth: '40%'
        }
      ]
    };
  };

  // 模拟调价操作
  const handlePriceAdjustment = () => {
    alert(`已将${alertData.targetDate}的价格从¥${alertData.currentPrice}调整为¥${alertData.suggestedPrice}`);
    navigate(-1);
  };

  const typeInfo = getPriceChangeTypeIcon(alertData.type);

  // 获取按房型筛选后的竞品数据
  const getFilteredCompetitors = () => {
    if (competitorRoomTypeId === 'all') {
      // 如果选择"全部房型"，则合并相同酒店不同房型的数据，保留每个竞品酒店的标准房型数据
      return alertData.competitors.filter(comp => comp.roomTypeId === '1');
    } else {
      // 筛选出特定房型的竞品数据
      return alertData.competitors.filter(comp => comp.roomTypeId === competitorRoomTypeId);
    }
  };

  return (
    <div style={{ 
      padding: '16px', 
      backgroundColor: '#f5f5f5', 
      minHeight: '100vh',
      maxWidth: '500px',
      margin: '0 auto'
    }}>
      {/* 顶部导航栏 */}
      <Card style={{ marginBottom: 16, borderRadius: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space align="center">
            {typeInfo.icon && React.cloneElement(typeInfo.icon as React.ReactElement, { 
              style: { fontSize: 24, color: typeInfo.color } 
            })}
            <Title level={4} style={{ margin: 0 }}>调价提醒</Title>
          </Space>
          <Button 
            type="text" 
            icon={<LeftOutlined />} 
            onClick={() => navigate(-1)}
            style={{ padding: 0 }}
          >
            返回
          </Button>
        </div>
      </Card>

      {/* 核心提醒信息区 */}
      <Card 
        style={{ 
          marginBottom: '16px', 
          borderRadius: '8px', 
          borderLeft: `4px solid ${typeInfo.color}`,
          backgroundColor: alertData.severity === 'high' ? '#FFF1F0' : (
            alertData.severity === 'medium' ? '#FFFBE6' : '#F6FFED'
          ),
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}
      >
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {/* 每条数据单独一行 */}
          <div style={{ padding: '4px 0' }}>
            <Text>通知标题: <Text strong>{alertData.title}</Text></Text>
          </div>
          
          <div style={{ padding: '4px 0' }}>
            <Text>通知类型: <Tag color={typeInfo.color}>{typeInfo.text}</Tag></Text>
          </div>
          
          <div style={{ padding: '4px 0' }}>
            <Text>通知内容: <Text type="warning" strong>{alertData.content}</Text></Text>
          </div>
          
          <div style={{ padding: '4px 0' }}>
            <Text>通知时间: {alertData.time}</Text>
          </div>
          
          <div style={{ padding: '4px 0' }}>
            <Text>门店名称: {alertData.store}</Text>
          </div>
        </Space>
      </Card>

      {/* 价格调整建议区 - 隐藏该部分 */}
      {false && (
        <Card 
          style={{ 
            marginBottom: '16px', 
            borderRadius: '8px',
            backgroundColor: '#f6ffed',
            borderLeft: '4px solid #52c41a',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}
        >
          <div style={{ padding: '12px 0' }}>
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ fontSize: 16 }}>价格调整建议</Text>
            </div>
            
            <Row gutter={[16, 16]} align="middle">
              <Col span={8}>
                <Statistic
                  title="当前价格"
                  value={alertData.currentPrice}
                  precision={0}
                  prefix="¥"
                  style={{ textAlign: 'center' }}
                />
              </Col>
              <Col span={8}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 'bold', color: '#ff4d4f' }}>
                    {alertData.changeRatio > 0 && '+'}{alertData.changeRatio}%
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {alertData.changeAmount > 0 && '+'}{alertData.changeAmount}元
                  </Text>
                </div>
              </Col>
              <Col span={8}>
                <Statistic
                  title="建议价格"
                  value={alertData.suggestedPrice}
                  precision={0}
                  prefix="¥"
                  valueStyle={{ color: '#ff4d4f', fontWeight: 'bold' }}
                  style={{ textAlign: 'center' }}
                />
              </Col>
            </Row>

            <Divider style={{ margin: '16px 0' }} />
            
            <div>
              <Text type="secondary">调整理由: </Text>
              <Text>{alertData.reasonSummary}</Text>
            </div>
          </div>
        </Card>
      )}

      {/* 智能建议区 */}
      <Card 
        title={
          <Space>
            <InfoCircleOutlined />
            <span>智能建议</span>
          </Space>
        }
        style={{ marginBottom: 16, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
        headStyle={{ padding: '0 12px' }}
        bodyStyle={{ padding: 12 }}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Card
            style={{ 
              backgroundColor: '#E6F7FF',
              borderColor: '#91D5FF'
            }}
            bodyStyle={{ padding: 12 }}
            size="small"
          >
            <Text strong>建议{dayjs(alertData.targetDate).format('MM月DD日')}上调价格：¥{alertData.currentPrice} → ¥{alertData.suggestedPrice}（+{alertData.changeRatio}%）</Text>
            <div style={{ marginTop: 12, lineHeight: '1.5', color: 'rgba(0, 0, 0, 0.65)', fontSize: 13 }}>
              
              {alertData.type === 'timePoint' && (
                <div style={{ marginBottom: 4 }}>● 时间点因素：周末高峰期间需求增加，价格弹性降低</div>
              )}
              {alertData.type === 'competitor' && (
                <div style={{ marginBottom: 4 }}>● 竞品因素：主要竞品已上调价格，市场接受度高</div>
              )}
              {alertData.type === 'roomStatus' && (
                <div style={{ marginBottom: 4 }}>● 房态因素：当前库存紧张，提价可控制售卖速度</div>
              )}
              <div style={{ marginBottom: 4 }}>● 采纳建议预计可增加¥{alertData.revenueImpact[1].additionalRevenue}收益</div>
            </div>
            <div style={{ fontSize: 12, color: '#8C8C8C', marginTop: 4 }}>
              建议生成时间: {alertData.time}
            </div>
          </Card>
          
          <Button 
            type="primary" 
            block
            onClick={handlePriceAdjustment}
          >
            一键调价
          </Button>
        </Space>
      </Card>

      {/* 价格趋势图 */}
      <Card 
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '16px' }}>价格趋势分析</span>
            <Select
              size="small"
              style={{ width: 120 }}
              value={selectedRoomTypeId}
              onChange={(value) => setSelectedRoomTypeId(value)}
              options={[
                { value: 'all', label: '全部房型' },
                ...alertData.roomTypes.map(type => ({
                  value: type.id,
                  label: type.name
                }))
              ]}
            />
          </div>
        } 
        style={{ marginBottom: '16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
      >
        <ReactECharts 
          option={getPriceTrendOptions()} 
          style={{ height: '250px', width: '100%' }}
        />
        <div style={{ marginTop: '16px', color: '#8c8c8c', fontSize: '13px' }}>
          <Text type="secondary">* 红色标注日期为建议调价日期，图表显示{selectedRoomTypeId === 'all' ? '全部房型' : 
            alertData.roomTypes.find(item => item.id === selectedRoomTypeId)?.name || ''}的当前价格、建议价格与竞品均价对比</Text>
        </div>
      </Card>

      {/* 房型调价详情 - 移到价格趋势图后面 */}
      {alertData.roomTypes.length > 0 && (
        <Card 
          title={<span style={{ fontSize: '16px' }}>房型详情</span>} 
          style={{ marginBottom: '16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
        >
          <Table
            dataSource={alertData.roomTypes}
            pagination={false}
            size="small"
            columns={[
              {
                title: '房型名称',
                dataIndex: 'name',
                key: 'name',
                render: (text) => <Text strong>{text}</Text>
              },
              {
                title: '当前价格',
                dataIndex: 'currentPrice',
                key: 'currentPrice',
                render: (price) => <Text>¥{price}</Text>,
                align: 'center'
              },
              {
                title: '建议价格',
                dataIndex: 'suggestedPrice',
                key: 'suggestedPrice',
                render: (price) => <Text strong style={{ color: '#ff4d4f' }}>¥{price}</Text>,
                align: 'center'
              },
              {
                title: '调整幅度',
                dataIndex: 'changeRatio',
                key: 'changeRatio',
                render: (ratio) => <Text>{ratio > 0 ? '+' : ''}{ratio}%</Text>,
                align: 'center'
              },
              {
                title: '可用/总房数',
                key: 'roomStatus',
                render: (_, record) => (
                  <Text>
                    {record.roomsAvailable}/{record.totalRooms}
                    <span style={{ 
                      marginLeft: 4,
                      color: record.roomsAvailable / record.totalRooms < 0.2 ? '#ff4d4f' : 
                             record.roomsAvailable / record.totalRooms < 0.5 ? '#faad14' : '#52c41a'
                    }}>
                      ({Math.round(record.roomsAvailable / record.totalRooms * 100)}%)
                    </span>
                  </Text>
                ),
                align: 'center'
              }
            ]}
          />
          <div style={{ marginTop: '16px', color: '#8c8c8c', fontSize: '13px' }}>
            <Text type="secondary">* 不同房型根据需求弹性、库存情况和竞争情况有不同的调价建议</Text>
          </div>
        </Card>
      )}

      {/* 收益影响预测 */}
      <Card 
        title={<span style={{ fontSize: '16px' }}>收益影响预测</span>} 
        style={{ marginBottom: '16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
      >
        <ReactECharts 
          option={getRevenueImpactOptions()} 
          style={{ height: '250px', width: '100%' }}
        />
        <div style={{ marginTop: '16px', color: '#8c8c8c', fontSize: '13px' }}>
          <Text type="secondary">* 收益预测基于历史数据和市场需求弹性计算，显示不同调价策略的收益差异</Text>
        </div>
      </Card>

      {/* 竞品价格分析 */}
      {alertData.competitors.length > 0 && (
        <Card 
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '16px' }}>竞品价格分析</span>
              <Select
                size="small"
                style={{ width: 120 }}
                value={competitorRoomTypeId}
                onChange={(value) => setCompetitorRoomTypeId(value)}
                options={[
                  { value: 'all', label: '标准房型' },
                  ...alertData.roomTypes.map(type => ({
                    value: type.id,
                    label: type.name
                  }))
                ]}
              />
            </div>
          }
          style={{ marginBottom: '16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
        >
          <Table
            dataSource={getFilteredCompetitors()}
            pagination={false}
            size="small"
            columns={[
              {
                title: '竞品名称',
                dataIndex: 'name',
                key: 'name',
                render: (text) => <Text strong>{text}</Text>
              },
              {
                title: '当前价格',
                dataIndex: 'currentPrice',
                key: 'currentPrice',
                render: (price) => <Text>¥{price}</Text>,
                align: 'center'
              },
              {
                title: '价格变动',
                dataIndex: 'priceChange',
                key: 'priceChange',
                render: (change, record) => (
                  <Space>
                    {record.changeType === 'increase' && <ArrowUpOutlined style={{ color: '#ff4d4f' }} />}
                    {record.changeType === 'decrease' && <ArrowDownOutlined style={{ color: '#52c41a' }} />}
                    <Text 
                      style={{ 
                        color: record.changeType === 'increase' ? '#ff4d4f' : 
                               record.changeType === 'decrease' ? '#52c41a' : 'inherit'
                      }}
                    >
                      {change > 0 ? '+' : ''}{change}元
                    </Text>
                  </Space>
                ),
                align: 'center'
              },
              {
                title: '库存情况',
                key: 'roomStatus',
                render: (_, record) => (
                  record.roomsAvailable && record.totalRooms ? (
                    <Text>
                      {record.roomsAvailable}/{record.totalRooms}
                      <span style={{ 
                        marginLeft: 4,
                        color: record.roomsAvailable / record.totalRooms < 0.2 ? '#ff4d4f' : 
                               record.roomsAvailable / record.totalRooms < 0.5 ? '#faad14' : '#52c41a'
                      }}>
                        ({Math.round(record.roomsAvailable / record.totalRooms * 100)}%)
                      </span>
                    </Text>
                  ) : <Text>--/--</Text>
                ),
                align: 'center'
              }
            ]}
          />
          <div style={{ marginTop: '16px', color: '#8c8c8c', fontSize: '13px' }}>
            <Text type="secondary">* 分析范围为{alertData.store}周边{getFilteredCompetitors().length}家主要竞品，价格为{dayjs(alertData.targetDate).format('MM月DD日')}{competitorRoomTypeId === 'all' ? '标准房型' : alertData.roomTypes.find(type => type.id === competitorRoomTypeId)?.name || ''}</Text>
          </div>
        </Card>
      )}

      {/* 相关大事件信息 */}
      {alertData.events.length > 0 && (
        <Card 
          title={
            <Space>
              <BarChartOutlined />
              <span>相关大事件信息</span>
            </Space>
          }
          style={{ marginBottom: '16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
          headStyle={{ padding: '0 12px' }}
          bodyStyle={{ padding: 12 }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            {alertData.events.map(event => {
              // 根据事件类型获取颜色和图标
              const typeColor = getEventTypeIcon(event.type).color;
              const impactColor = getImpactColor(event.impact);
              const impactText = getImpactText(event.impact);
              
              return (
                <div 
                  key={event.id} 
                  style={{ 
                    borderLeft: `4px solid ${typeColor}`,
                    padding: '12px 16px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '4px',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <Space>
                      <Text strong style={{ fontSize: 16 }}>{event.name}</Text>
                      <Tag color={typeColor}>
                        {event.type}
                      </Tag>
                    </Space>
                    <Tag color={impactColor}>
                      影响度: {impactText}
                    </Tag>
                  </div>
                  
                  <div style={{ marginBottom: 8, color: 'rgba(0, 0, 0, 0.45)' }}>
                    日期: {dayjs(event.date).format('YYYY-MM-DD')}
                  </div>
                  
                  <div style={{ marginBottom: 0, fontSize: '14px' }}>
                    {event.description || `该事件可能对价格产生${impactText === '高' ? '重大' : impactText === '中' ? '中等' : '轻微'}影响，建议关注市场动态`}
                  </div>
                </div>
              );
            })}
          </Space>
          
          <div style={{ marginTop: 16, color: '#8c8c8c', fontSize: 13 }}>
            * 大事件可能导致市场需求变化，是本次调价建议的重要参考因素
          </div>
        </Card>
      )}
    </div>
  );
};

export default PriceChangeAlert; 